import React from "react";
import PropTypes from "prop-types";
//Limitless imports
import Chart from "react-apexcharts";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "@limitless/common/hooks";
import DashboardCommonTooltip from "./DashboardCommonTooltip";
import * as ReactDOMServer from "react-dom/server";
import { LoadingSpinner } from "@limitless/portal";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";

let axisColor = "#000",
    primaryColor = "#183A74",
    secondaryColor = "#D3638F",
    tertiaryColor = "#FFC369";

const useStyles = makeStyles(theme => {
    axisColor = theme.palette.text.secondary;
    primaryColor = theme.palette.graph.blue;
    secondaryColor = theme.palette.graph.pink;
    tertiaryColor = theme.palette.graph.yellow;

    return {
        container: {
            width: "100%"
        },
        chartContainer: {
            justifyContent: "center",
            alignItems: "center"
        }
    };
});

export const defaultChart = {
    height: 350,
    stacked: false,
    zoom: {
        enabled: false
    },
    toolbar: {
        show: false
    },
    foreColor: theme => theme.palette.text.primary
};
const DashboardChart = ({
    options,
    chartType = "area",
    series,
    categories = [],
    stacked = false,
    tickAmount = false,
    crosshairsShown = true,
    inverseOrder = false
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const defaultOptions = {
        chart: {
            ...defaultChart,
            stacked,
            events: {
                mouseMove: () => {
                    const crosshairs = document.querySelector(".apexcharts-xcrosshairs"),
                        marker = document.querySelector(".apexcharts-marker");
                    const tooltipVal = document.getElementsByClassName("apexcharts-tooltip");
                    let styles;
                    if (tooltipVal[0].innerText === "") {
                        styles = `
                        .apexcharts-xaxistooltip {
                          opacity: 0 !important;
                        }
                        .apexcharts-xcrosshairs {
                          opacity: 0 !important;
                        }
                        `;
                    } else {
                        styles = `
                        .apexcharts-xaxistooltip {
                          opacity: 1 !important;
                        }
                        .apexcharts-xcrosshairs {
                          opacity: 1 !important;
                        }
                        `;
                    }
                    const styleSheet = document.createElement("style");
                    styleSheet.innerText = styles;
                    document.head.appendChild(styleSheet);

                    const settings = { attributes: true },
                        observer = new MutationObserver(() => {
                            crosshairs?.setAttribute("y1", `${marker.cy.baseVal.value + marker.r.baseVal.value + 1}`);
                        });

                    observer?.observe(marker, settings);
                }
            }
        },

        colors: [primaryColor, secondaryColor, tertiaryColor],
        dataLabels: {
            enabled: false
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.45,
                opacityTo: 0.05
            }
        },
        yaxis: {
            labels: {
                formatter(val, index, series) {
                    const yValue = series?.globals?.maxY / 4;

                    return Math.round(yValue * index);
                },
                style: {
                    colors: axisColor
                },
                offsetX: 0
            },
            min: 0,
            max: max => {
                return max > 4 ? max + ((max * 15) / 100) : max > 0 && max < 4 ? 4 : 100;
            },
            tickAmount: 4,
            opposite: true
        },
        xaxis: {
            labels: {
                style: {
                    colors: axisColor
                }
            },
            tooltip: true,
            categories,
            crosshairs: {
                show: crosshairsShown ? true : false,
                position: "back",
                stroke: {
                    color: theme.palette.primary.dark,
                    width: 1,
                    dashArray: 5
                }
            },
            tickAmount: tickAmount ? 5 : categories.length,
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        tooltip: {
            custom: ({ dataPointIndex, w }) => {
                const data = w.globals.initialSeries[1].data[dataPointIndex];

                return data.value && w.globals.maxY !== 0
                    ? ReactDOMServer.renderToStaticMarkup(
                          <DashboardCommonTooltip
                              className="arrow_box_reward"
                              theme={theme}
                              date={data.date}
                              value={data.value}
                              valueText={t("earned on this day")}
                          />
                      )
                    : "";
            },

            theme: true
        },
        legend: {
            position: "top",
            horizontalAlign: "right",
            offsetX: 20,
            inverseOrder,
            markers: {
                offsetY: 2,
                offsetX: -2
            },
            itemMargin: {
                horizontal: 8,
                vertical: 0
            },
            onItemClick: {
                toggleDataSeries: false
            },
            onItemHover: {
                highlightDataSeries: true
            }
        },
        stroke: {
            curve: "straight"
        },
        ...options
    };

    return (
        <Grid container className={classes.chartContainer}>
            {React.useMemo(() => {
                return categories.length === 0 ? (
                    <LoadingSpinner size={30} color="primary" />
                ) : (
                    <Chart
                        options={defaultOptions}
                        series={series}
                        type={chartType}
                        className={classes.container}
                        height={320}
                    />
                );
            }, [series, axisColor, theme])}
        </Grid>
    );
};
DashboardChart.propTypes = {
    options: PropTypes.object,
    chartType: PropTypes.string,
    series: PropTypes.array,
    stacked: PropTypes.bool,
    crosshairsShown: PropTypes.bool,
    tickAmount: PropTypes.bool,
    inverseOrder: PropTypes.bool,
    categories: PropTypes.array
};
export default DashboardChart;
